/**
 * Phase 1.1 Ship 4a — Verified Member cutover banner.
 *
 * Per auditor B5 ("add an in-UI definition/cutover notice. A silent
 * VM definition change is risky"). Mounts on the R5 reports (Verified
 * Members, Sales by Rep, Event Attendance) so operators don't miss
 * the change.
 *
 * Reads vm_cutover_date + vm_show_legacy from system_config.
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../services/supabase";

interface VMCutoverConfig {
  cutoverDate: string | null;
  showLegacy: boolean;
}

const VMCutoverBanner: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const { data } = useQuery<VMCutoverConfig>({
    queryKey: ["vm-cutover-config-banner"],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from("system_config")
        .select("key, value")
        .in("key", ["vm_cutover_date", "vm_show_legacy"]);
      const map = new Map(
        (rows ?? []).map((r: { key: string; value: string }) => [
          r.key,
          r.value,
        ]),
      );
      return {
        cutoverDate: map.get("vm_cutover_date") ?? null,
        showLegacy: map.get("vm_show_legacy") !== "false",
      };
    },
    staleTime: 5 * 60_000,
  });

  if (!data?.cutoverDate) {
    return null;
  }

  const pastCutover = new Date() > new Date(data.cutoverDate);
  const stateLabel = pastCutover
    ? "Verified Member metric switched"
    : "Verified Member metric is changing";

  // Phase 1.1 post-deploy: legacy column visibility follows the
  // same rule as EventCloseRateCard — pre-cutover it's always
  // shown, post-cutover only when operator explicitly overrode
  // vm_show_legacy=true. The seeded default flipped to 'false'
  // so the auto-hide actually fires when cutover_date passes.
  const legacyMessage = pastCutover
    ? data.showLegacy
      ? "Legacy voucher-based count is still shown (operator override)."
      : "Legacy voucher-based count is now hidden. Operator can re-enable via vm_show_legacy=true."
    : `Legacy voucher-based count is shown alongside until ${data.cutoverDate}.`;

  return (
    <>
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm mb-4 flex items-start gap-3">
        <span className="text-amber-700 mt-0.5" aria-hidden>
          ⓘ
        </span>
        <div className="flex-1">
          <p className="font-medium text-amber-900">{stateLabel}.</p>
          <p className="text-amber-800 mt-1">
            {pastCutover
              ? `As of ${data.cutoverDate}, the new attendee + Zuora-confirmed definition is primary.`
              : `Starting ${data.cutoverDate}, the new attendee + Zuora-confirmed definition becomes primary.`}{" "}
            {legacyMessage}
          </p>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="mt-1 text-amber-900 underline hover:text-amber-700"
          >
            Learn more
          </button>
        </div>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-900">
              Verified Member definition change
            </h2>
            <div className="space-y-3 text-sm text-gray-700">
              <p>
                <strong>Old definition (legacy):</strong> A unique contact who
                received a voucher tied to one of this event's CTAs. Voucher
                status excluded cancelled and test entries.
              </p>
              <p>
                <strong>New definition:</strong> A unique contact who registered
                via a CTA URL (the rep's QR code or the event's generic CTA) AND
                whose Zuora subscription has landed in our system within the
                event-relative window (event start − 1 day through event end +
                30 days).
              </p>
              <p>
                <strong>Why the change?</strong> Voucher-issued count was a
                proxy for "membership purchase happened." Most of our signups
                don't issue a voucher (subscription- only signups are the
                majority), so the legacy count systematically undercounted. The
                new definition uses Zuora as the membership truth source.
              </p>
              <p>
                <strong>Cutover date:</strong> {data.cutoverDate}
              </p>
              <p>
                <strong>Pending Zuora confirmation:</strong> The Zuora webhook
                can take minutes to hours to land. New signups appear as
                "pending" until confirmed, then reclassify automatically. They
                are NOT included in close-rate calculations until confirmed.
              </p>
              <p>
                <strong>Commission impact:</strong> Commission calculations
                remain on the legacy proxy until finance approves the switch.
                Reports surface the new metric for visibility only.
              </p>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VMCutoverBanner;
